import type { User } from "@supabase/supabase-js";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CalendarDays,
  Check,
  ChevronRight,
  Eye,
  Fish,
  Heart,
  Home,
  Leaf,
  LockKeyhole,
  LogOut,
  Mail,
  MapPin,
  Menu,
  NotebookPen,
  Pencil,
  Plane,
  Plus,
  RefreshCw,
  Ruler,
  Scale,
  Search,
  ShieldCheck,
  Sparkles,
  Sprout,
  Star,
  Trash2,
  Users,
  Waves,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  addExperienceStep,
  addGardenIdea,
  addQuickCatch,
  addWaterObservation,
  createExperience,
  createWorkspace,
  deleteExperienceStep,
  inviteMember,
  loadAppData,
  planTrip,
  reorderExperienceSteps,
  revealExperience,
  saveReflection,
  saveTripPlan,
  suggestTrips,
  updateExperience,
  updateExperienceStep,
} from "./lib/data";
import { supabase } from "./lib/supabase";
import type {
  AiOption,
  AiPlan,
  AiWish,
  AppData,
  Experience,
  ExperienceInput,
  ExperienceStep,
  ExperienceStepInput,
  ExperienceVisibility,
  ModuleKey,
} from "./types";

type Tab = "home" | "experiences" | "garden" | "fishing" | "more";
type ModalName =
  | "new-experience"
  | "ai-plan"
  | "edit-experience"
  | "step"
  | "reflection"
  | "garden-idea"
  | "water"
  | "catch"
  | "invite"
  | null;
type ScreenState =
  | { kind: "loading" }
  | { kind: "signed-out" }
  | { kind: "onboarding"; user: User }
  | { kind: "ready"; user: User; data: AppData }
  | { kind: "fatal"; message: string };

const moduleNames: Record<ModuleKey, string> = {
  core: "Grunnur",
  experiences: "Upplifanir",
  garden: "Garðurinn",
  fishing: "Veiði",
};

const navigation: {
  id: Tab;
  label: string;
  icon: LucideIcon;
  module?: ModuleKey;
}[] = [
  { id: "home", label: "Heim", icon: Home },
  { id: "experiences", label: "Upplifanir", icon: Sparkles, module: "experiences" },
  { id: "garden", label: "Garður", icon: Sprout, module: "garden" },
  { id: "fishing", label: "Veiði", icon: Fish, module: "fishing" },
  { id: "more", label: "Meira", icon: Menu },
];

const monthNames = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maí",
  "jún",
  "júl",
  "ágú",
  "sep",
  "okt",
  "nóv",
  "des",
];

function dateParts(value: string) {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return { day, month: monthNames[(month || 1) - 1] ?? "", year };
}

function formatDate(value: string, includeTime = false) {
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("is-IS", {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

const fullMonthNames = ["janúar", "febrúar", "mars", "apríl", "maí", "júní", "júlí", "ágúst", "september", "október", "nóvember", "desember"];
const weekdayNames = ["sunnudagur", "mánudagur", "þriðjudagur", "miðvikudagur", "fimmtudagur", "föstudagur", "laugardagur"];

function formatDateRange(start: string, end: string | null) {
  if (!end || end === start) return formatDate(start);
  const [fromYear, fromMonth, fromDay] = start.split("-").map(Number);
  const [toYear, toMonth, toDay] = end.split("-").map(Number);
  const to = `${toDay}. ${fullMonthNames[toMonth - 1]} ${toYear}`;
  if (fromYear === toYear && fromMonth === toMonth) return `${fromDay}.–${to}`;
  if (fromYear === toYear) return `${fromDay}. ${fullMonthNames[fromMonth - 1]} – ${to}`;
  return `${fromDay}. ${fullMonthNames[fromMonth - 1]} ${fromYear} – ${to}`;
}

function addDays(value: string, days: number) {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

// Allir dagar upplifunar, frá fyrsta til síðasta dags.
function experienceDays(experience: Pick<Experience, "eventDate" | "endDate">) {
  const days = [experience.eventDate];
  if (!experience.endDate) return days;
  while (days.length < 60 && days[days.length - 1] < experience.endDate) {
    days.push(addDays(days[days.length - 1], 1));
  }
  return days;
}

function weekdayLabel(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${weekdayNames[weekday]} ${day}. ${fullMonthNames[month - 1]}`;
}

// Dagskrárliðir í birtingarröð: eftir degi, svo eftir sort_order.
function orderedSteps(experience: Experience) {
  return [...experience.steps].sort((a, b) => {
    const dayA = a.dayDate ?? experience.eventDate;
    const dayB = b.dayDate ?? experience.eventDate;
    return dayA === dayB ? a.sortOrder - b.sortOrder : dayA < dayB ? -1 : 1;
  });
}

function localDateTimeValue(date = new Date()) {
  const adjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return adjusted.toISOString().slice(0, 16);
}

function todayValue() {
  return localDateTimeValue().slice(0, 10);
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function errorText(caught: unknown) {
  const raw = caught instanceof Error ? caught.message : "Eitthvað fór úrskeiðis.";
  if (/rate limit/i.test(raw)) return "Of margar tilraunir. Bíddu aðeins og reyndu aftur.";
  if (/invalid login|email not confirmed/i.test(raw)) return "Innskráning tókst ekki. Athugaðu tölvupóstinn og reyndu aftur.";
  return raw;
}

export default function App() {
  const [screen, setScreen] = useState<ScreenState>({ kind: "loading" });
  const [tab, setTab] = useState<Tab>("home");
  const [modal, setModal] = useState<ModalName>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingStep, setEditingStep] = useState<ExperienceStep | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  async function hydrate(user: User) {
    try {
      const result = await loadAppData(user);
      if (result.kind === "onboarding") setScreen({ kind: "onboarding", user });
      else setScreen({ kind: "ready", user, data: result.data });
    } catch (caught) {
      setScreen({ kind: "fatal", message: errorText(caught) });
    }
  }

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!active) return;
      if (sessionError) setScreen({ kind: "fatal", message: sessionError.message });
      else if (data.session?.user) void hydrate(data.session.user);
      else setScreen({ kind: "signed-out" });
    });
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active || event === "INITIAL_SESSION") return;
      if (event === "SIGNED_OUT" || !session?.user) setScreen({ kind: "signed-out" });
      else window.setTimeout(() => void hydrate(session.user), 0);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  function flash(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 3600);
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  }

  async function refresh(message?: string) {
    if (screen.kind !== "ready") return;
    const result = await loadAppData(screen.user);
    if (result.kind === "ready") setScreen({ ...screen, data: result.data });
    if (message) flash(message);
  }

  function changeTab(next: Tab) {
    setSelectedId(null);
    setTab(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (screen.kind === "loading") return <LoadingScreen />;
  if (screen.kind === "signed-out") return <AuthScreen />;
  if (screen.kind === "fatal") {
    return (
      <CenteredCard icon={<Heart size={20} />} eyebrow="Tengingin hikstaði" title="Prófum aftur">
        <p>{screen.message}</p>
        <button className="button button-primary full" onClick={() => window.location.reload()}>
          <RefreshCw size={16} /> Endurhlaða
        </button>
      </CenteredCard>
    );
  }
  if (screen.kind === "onboarding") {
    return (
      <OnboardingScreen
        email={screen.user.email ?? ""}
        busy={busy}
        error={error}
        onSubmit={(name) =>
          run(async () => {
            await createWorkspace(name);
            await hydrate(screen.user);
          })
        }
        onSignOut={() => void supabase.auth.signOut()}
      />
    );
  }

  const { data, user } = screen;
  const selected = data.experiences.find((item) => item.id === selectedId) ?? null;
  const visibleNavigation = navigation.filter(
    (item) => !item.module || data.modules.includes(item.module),
  );

  return (
    <main className="app-shell">
      <header className="topbar">
        <button className="brand brand-button" onClick={() => changeTab("home")} aria-label="Fara heim">
          <span className="brand-mark"><Heart size={18} /></span>
          <span>
            <strong>Líf og jafnvægi</strong>
            <small>{data.workspace.name}</small>
          </span>
        </button>
        <button className="member-button" onClick={() => changeTab("more")}>
          <Users size={15} /> {data.members.length}
        </button>
      </header>

      <div className="page-wrap">
        <div className="page-grid">
          <nav className="side-nav" aria-label="Aðalvalmynd">
            {visibleNavigation.map(({ id, label, icon: Icon }) => (
              <button key={id} className={tab === id && !selected ? "active" : ""} onClick={() => changeTab(id)}>
                <Icon size={18} /> {label}
              </button>
            ))}
            <p className="side-note">Fá skref, skýr friðhelgi og meiri tími í lífið sjálft.</p>
          </nav>

          <section className="content">
            {selected ? (
              <ExperienceDetail
                experience={selected}
                busy={busy}
                onBack={() => setSelectedId(null)}
                onReflect={() => { setError(""); setModal("reflection"); }}
                onEdit={() => { setError(""); setModal("edit-experience"); }}
                onAddStep={() => { setError(""); setEditingStep(null); setModal("step"); }}
                onEditStep={(step) => { setError(""); setEditingStep(step); setModal("step"); }}
                onDeleteStep={(step) => {
                  if (!window.confirm(`Eyða „${step.title}“ úr dagskránni?`)) return;
                  void run(async () => {
                    await deleteExperienceStep(step.id);
                    await refresh("Liðnum var eytt.");
                  });
                }}
                onMoveStep={(stepIds) => run(async () => {
                  await reorderExperienceSteps(selected.id, stepIds);
                  await refresh();
                })}
                onReveal={() => run(async () => {
                  await revealExperience(selected.id);
                  await refresh("Upplifuninni hefur verið deilt.");
                })}
              />
            ) : tab === "home" ? (
              <HomeView
                data={data}
                onTab={changeTab}
                onOpenExperience={(id) => { setTab("experiences"); setSelectedId(id); }}
                onNewExperience={() => { setError(""); setModal("new-experience"); }}
              />
            ) : tab === "experiences" ? (
              <ExperiencesView
                experiences={data.experiences}
                onNew={() => { setError(""); setModal("new-experience"); }}
                onAiPlan={() => { setError(""); setModal("ai-plan"); }}
                onOpen={setSelectedId}
              />
            ) : tab === "garden" ? (
              <GardenView
                data={data}
                onNewIdea={() => { setError(""); setModal("garden-idea"); }}
              />
            ) : tab === "fishing" ? (
              <FishingView
                data={data}
                onWater={() => { setError(""); setModal("water"); }}
                onCatch={() => { setError(""); setModal("catch"); }}
              />
            ) : (
              <MoreView
                data={data}
                email={user.email ?? ""}
                onInvite={() => { setError(""); setModal("invite"); }}
                onSignOut={() => void supabase.auth.signOut()}
              />
            )}
          </section>
        </div>
      </div>

      <nav className={`bottom-nav items-${visibleNavigation.length}`} aria-label="Aðalvalmynd">
        {visibleNavigation.map(({ id, label, icon: Icon }) => (
          <button key={id} className={tab === id && !selected ? "active" : ""} onClick={() => changeTab(id)}>
            <Icon size={18} /> <span>{label}</span>
          </button>
        ))}
      </nav>

      {modal === "new-experience" && (
        <NewExperienceModal busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(input) =>
          run(async () => {
            await createExperience({ workspaceId: data.workspace.id, ...input });
            await refresh("Upplifunin er vistuð.");
            setModal(null);
            setTab("experiences");
          })
        } />
      )}
      {modal === "ai-plan" && (
        <AiPlanModal onClose={() => setModal(null)} onSave={async (wish, option, plan) => {
          const id = await saveTripPlan(data.workspace.id, wish, option, plan);
          await refresh("Ferðin er vistuð sem upplifun.");
          setModal(null);
          setTab("experiences");
          setSelectedId(id);
        }} />
      )}
      {modal === "edit-experience" && selected && (
        <EditExperienceModal experience={selected} busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(input) =>
          run(async () => {
            await updateExperience(selected.id, input);
            await refresh("Upplifunin er uppfærð.");
            setModal(null);
          })
        } />
      )}
      {modal === "step" && selected && (
        <StepModal experience={selected} step={editingStep} busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(input) =>
          run(async () => {
            if (editingStep) await updateExperienceStep(editingStep.id, input);
            else await addExperienceStep(selected, input);
            await refresh(editingStep ? "Liðurinn er uppfærður." : "Liðnum var bætt við.");
            setModal(null);
          })
        } />
      )}
      {modal === "reflection" && selected && (
        <ReflectionModal experience={selected} busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(input) =>
          run(async () => {
            await saveReflection(selected.id, input);
            await refresh("Þín minning er vistuð.");
            setModal(null);
          })
        } />
      )}
      {modal === "garden-idea" && (
        <GardenIdeaModal busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(text) =>
          run(async () => {
            await addGardenIdea(data.workspace.id, text);
            await refresh("Garðhugmyndin er vistuð.");
            setModal(null);
          })
        } />
      )}
      {modal === "water" && (
        <WaterModal locations={data.fishingLocations} busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(input) =>
          run(async () => {
            await addWaterObservation({ workspaceId: data.workspace.id, ...input });
            await refresh("Aðstæðurnar eru skráðar.");
            setModal(null);
          })
        } />
      )}
      {modal === "catch" && (
        <CatchModal locations={data.fishingLocations} busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(input) =>
          run(async () => {
            await addQuickCatch({ workspaceId: data.workspace.id, ...input });
            await refresh("Aflinn er kominn í bókina.");
            setModal(null);
          })
        } />
      )}
      {modal === "invite" && (
        <InviteModal busy={busy} error={error} onClose={() => setModal(null)} onSubmit={(email, role) =>
          run(async () => {
            await inviteMember(data.workspace.id, email, role);
            flash(`Boð er tilbúið fyrir ${email}.`);
            setModal(null);
          })
        } />
      )}
      {toast && <output className="toast">{toast}</output>}
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <div className="loading-mark"><Heart size={25} /></div>
      <h1>Líf og jafnvægi</h1>
      <p>Sæki okkar rými…</p>
      <span className="loader" />
    </main>
  );
}

function CenteredCard({
  icon,
  eyebrow,
  title,
  children,
}: {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <main className="centered-page">
      <section className="centered-card">
        <span className="brand-mark">{icon}</span>
        <p className="eyebrow centered-eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {children}
      </section>
    </main>
  );
}

function AuthScreen() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const { error: signInError } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (signInError) setError(errorText(signInError));
    else setSent(true);
  }

  return (
    <main className="auth-page">
      <section className="auth-story">
        <div className="auth-brand"><span className="brand-mark"><Heart size={18} /></span> Líf og jafnvægi</div>
        <div>
          <p className="hero-kicker"><Sparkles size={13} /> Okkar rými</p>
          <h1>Tíminn okkar á að fara í lífið sjálft.</h1>
          <p>Upplifanir, garðurinn og veiðin á einum rólegum stað — fyrir það sem skiptir máli.</p>
        </div>
        <small>Sjálfstætt app · Gögn varin með persónulegri innskráningu</small>
      </section>
      <section className="auth-panel">
        <div className="auth-box">
          <p className="eyebrow">Velkomin/n</p>
          <h2>{sent ? "Athugaðu póstinn" : "Komdu inn"}</h2>
          {sent ? (
            <div className="sent-state">
              <span><Mail size={27} /></span>
              <p>Við sendum öruggan innskráningartengil á <strong>{email}</strong>.</p>
              <p className="fine-print">Tengillinn opnar appið — ekkert lykilorð þarf.</p>
              <button className="text-button" onClick={() => setSent(false)}>Nota annað netfang</button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <p className="auth-copy">Sláðu inn netfangið þitt. Þú færð einn öruggan tengil í pósthólfið.</p>
              <div className="field">
                <label htmlFor="email">Netfang</label>
                <input id="email" type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nafn@netfang.is" />
              </div>
              {error && <p className="error-message">{error}</p>}
              <button className="button button-primary full" disabled={busy}>
                <Mail size={16} /> {busy ? "Sendi…" : "Senda mér innskráningartengil"}
              </button>
              <p className="privacy-note"><ShieldCheck size={14} /> Aðeins fólk sem hefur fengið boð sér sameiginleg gögn.</p>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function OnboardingScreen({
  email,
  busy,
  error,
  onSubmit,
  onSignOut,
}: {
  email: string;
  busy: boolean;
  error: string;
  onSubmit: (name: string) => void;
  onSignOut: () => void;
}) {
  const [name, setName] = useState("");
  return (
    <CenteredCard icon={<Heart size={20} />} eyebrow="Nýtt rými" title="Hvað á rýmið að heita?">
      <p>Engin biðröð fannst fyrir <strong>{email}</strong>. Þú getur stofnað þitt eigið rými hér.</p>
      <form onSubmit={(event) => { event.preventDefault(); onSubmit(name); }}>
        <div className="field">
          <label htmlFor="workspace-name">Heiti</label>
          <input id="workspace-name" required maxLength={100} value={name} onChange={(event) => setName(event.target.value)} placeholder="t.d. Heimilið okkar" />
        </div>
        {error && <p className="error-message">{error}</p>}
        <button className="button button-primary full" disabled={busy}>{busy ? "Stofna…" : "Stofna rými"}</button>
      </form>
      <button className="text-button" onClick={onSignOut}>Skrá út og nota annað netfang</button>
    </CenteredCard>
  );
}

function HomeView({
  data,
  onTab,
  onOpenExperience,
  onNewExperience,
}: {
  data: AppData;
  onTab: (tab: Tab) => void;
  onOpenExperience: (id: string) => void;
  onNewExperience: () => void;
}) {
  const featured = data.experiences[0];
  const reflections = data.experiences.reduce((sum, item) => sum + item.reflections.length, 0);
  return (
    <>
      <section className="hero">
        <span className="hero-kicker"><Sparkles size={13} /> Okkar rými</span>
        <h1>Meiri tími í það sem skiptir máli.</h1>
        <p>Hugmyndir, skipulag, aðstæður og minningar á einum stað — án þess að leita og bera saman aftur og aftur.</p>
        <div className="hero-actions">
          {data.modules.includes("experiences") && <button className="button button-light" onClick={onNewExperience}><Plus size={16} /> Ný upplifun</button>}
          {data.modules.includes("fishing") && <button className="button button-hero-ghost" onClick={() => onTab("fishing")}><Fish size={16} /> Opna Veiði</button>}
        </div>
      </section>

      <div className="module-strip">
        {data.modules.filter((module) => module !== "core").map((module) => {
          const Icon = module === "experiences" ? Sparkles : module === "garden" ? Sprout : Fish;
          return (
            <button key={module} onClick={() => onTab(module === "experiences" ? "experiences" : module === "garden" ? "garden" : "fishing")}>
              <span><Icon size={18} /></span><strong>{moduleNames[module]}</strong><ChevronRight size={15} />
            </button>
          );
        })}
      </div>

      <div className="grid-two">
        <section className="card card-pad">
          <div className="section-row"><p className="mini-label">Nýjasta minningin</p>{featured && <button className="text-button" onClick={() => onTab("experiences")}>Sjá allt</button>}</div>
          {featured ? <ExperienceCard experience={featured} onOpen={onOpenExperience} /> : <Empty title="Fyrsta upplifunin bíður" text="Skráðu hugmynd eða dag sem þið viljið muna." />}
        </section>
        <section className="card card-pad overview-card">
          <p className="mini-label">Yfirlit heimilisins</p>
          <div className="number-row">
            <div className="number-cell"><strong>{data.experiences.length}</strong><span>upplifanir</span></div>
            <div className="number-cell"><strong>{data.gardenIdeas.length}</strong><span>garðhugmyndir</span></div>
            <div className="number-cell"><strong>{data.catchLogs.length}</strong><span>aflaskrár</span></div>
          </div>
          <div className="soft-row"><span className="soft-icon"><Users size={17} /></span><div><h3>{data.members.length} tengd</h3><p>{data.members.map((member) => member.displayName).join(" · ") || "Rýmið er tilbúið."}</p></div></div>
          <div className="soft-row"><span className="soft-icon"><NotebookPen size={17} /></span><div><h3>{reflections} minningar</h3><p>Hver varðveitir sína sýn án þess að skrifa yfir hinn.</p></div></div>
        </section>
      </div>
    </>
  );
}

function PageHeading({ eyebrow, title, text, action }: { eyebrow: string; title: string; text: string; action?: ReactNode }) {
  return (
    <div className="content-head">
      <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p className="subtitle">{text}</p></div>
      {action}
    </div>
  );
}

function ExperiencesView({ experiences, onNew, onAiPlan, onOpen }: { experiences: Experience[]; onNew: () => void; onAiPlan: () => void; onOpen: (id: string) => void }) {
  return (
    <>
      <PageHeading eyebrow="Okkar stundir" title="Upplifanir" text="Frá fyrstu hugmynd til sameiginlegrar minningar — með óvænt atriði örugg hjá skipuleggjandanum." action={<div className="step-toolbar"><button className="button button-ghost" onClick={onAiPlan}><Sparkles size={16} /> Gervigreind skipuleggur</button><button className="button button-primary" onClick={onNew}><Plus size={16} /> Ný</button></div>} />
      <div className="stack">
        {experiences.length ? experiences.map((experience) => <ExperienceCard key={experience.id} experience={experience} onOpen={onOpen} />) : <Empty title="Engin upplifun enn" text="Skráðu fyrstu hugmyndina þegar hún kviknar." action={<button className="button button-primary" onClick={onNew}>Skrá upplifun</button>} />}
      </div>
    </>
  );
}

function ExperienceCard({ experience, onOpen }: { experience: Experience; onOpen: (id: string) => void }) {
  const date = dateParts(experience.eventDate);
  return (
    <button className="experience-card" onClick={() => onOpen(experience.id)}>
      <span className="date-block"><strong>{date.day}</strong><span>{date.month} {date.year}</span></span>
      <span className="experience-copy">
        <h3>{experience.title}</h3>
        <p>{experience.introText || experience.occasion || "Upplifun sem bíður eftir að verða minning."}</p>
        <span className="pill-row">
          {experience.visibility === "surprise" && !experience.revealedAt && <span className="pill clay"><LockKeyhole size={11} /> Óvænt</span>}
          {experience.endDate && <span className="pill"><CalendarDays size={11} /> {experienceDays(experience).length} dagar</span>}
          <span className="pill">{experience.steps.length} atriði</span>
          {experience.reflections.length > 0 && <span className="pill wine">{experience.reflections.length} minningar</span>}
        </span>
      </span>
      <ChevronRight size={19} />
    </button>
  );
}

function ExperienceDetail({ experience, busy, onBack, onReflect, onReveal, onEdit, onAddStep, onEditStep, onDeleteStep, onMoveStep }: { experience: Experience; busy: boolean; onBack: () => void; onReflect: () => void; onReveal: () => void; onEdit: () => void; onAddStep: () => void; onEditStep: (step: ExperienceStep) => void; onDeleteStep: (step: ExperienceStep) => void; onMoveStep: (stepIds: string[]) => void }) {
  const mine = experience.reflections.find((reflection) => reflection.isMine);
  const [editing, setEditing] = useState(false);
  const steps = orderedSteps(experience);
  const days = experienceDays(experience);
  const multiDay = days.length > 1;
  const groups = multiDay
    ? days.map((day, index) => ({ day, label: `Dagur ${index + 1} · ${weekdayLabel(day)}`, steps: steps.filter((step) => (step.dayDate ?? experience.eventDate) === day) }))
        .concat([{ day: "", label: "Utan ferðardaga", steps: steps.filter((step) => step.dayDate && !days.includes(step.dayDate)) }])
        .filter((group) => group.day || group.steps.length)
    : [{ day: experience.eventDate, label: "", steps }];

  function move(step: ExperienceStep, direction: -1 | 1) {
    const group = groups.find((item) => item.steps.includes(step));
    if (!group) return;
    const index = group.steps.indexOf(step);
    const neighbour = group.steps[index + direction];
    if (!neighbour) return;
    const ids = steps.map((item) => item.id);
    const a = ids.indexOf(step.id);
    const b = ids.indexOf(neighbour.id);
    [ids[a], ids[b]] = [ids[b], ids[a]];
    onMoveStep(ids);
  }

  return (
    <>
      <section className="detail-hero">
        <div className="detail-topline">
          <button className="back-button" onClick={onBack}><ArrowLeft size={15} /> Til baka</button>
          <span className="hero-kicker"><CalendarDays size={12} /> {formatDateRange(experience.eventDate, experience.endDate)}</span>
        </div>
        <h1>{experience.title}</h1>
        <p>{experience.introText || experience.occasion}</p>
        <div className="hero-actions">
          <button className="button button-light" onClick={onReflect}><NotebookPen size={16} /> {mine ? "Breyta minni minningu" : "Skrá mína minningu"}</button>
          {experience.canReveal && <button className="button button-hero-ghost" disabled={busy} onClick={onReveal}><Eye size={16} /> Afhjúpa</button>}
          {experience.canManage && <button className="button button-hero-ghost" onClick={onEdit}><Pencil size={16} /> Breyta</button>}
        </div>
      </section>

      {experience.privateNote && <section className="private-banner"><LockKeyhole size={17} /><div><strong>Aðeins fyrir skipuleggjanda</strong><p>{experience.privateNote}</p></div></section>}

      <section className="card card-pad detail-section">
        <div className="section-row">
          <p className="mini-label">Dagskráin</p>
          {experience.canManage && (
            <div className="step-toolbar">
              {steps.length > 0 && <button className={`button button-ghost small ${editing ? "active" : ""}`} onClick={() => setEditing(!editing)}>{editing ? <><Check size={14} /> Búið</> : <><Pencil size={14} /> Raða og breyta</>}</button>}
              <button className="button button-primary small" onClick={onAddStep}><Plus size={14} /> Liður</button>
            </div>
          )}
        </div>
        {steps.length ? groups.map((group) => (
          <div className="day-group" key={group.day || "outside"}>
            {group.label && <h3 className="day-label">{group.label}</h3>}
            {group.steps.length ? (
              <div className="timeline">
                {group.steps.map((step, index) => (
                  <div className="timeline-step" key={step.id}>
                    <div className="time-label">{step.timeLabel || `#${index + 1}`}</div>
                    <div>
                      <span className="chapter">{step.chapter}</span><h3>{step.title}</h3><p className="step-note"><LinkedText text={step.note} /></p>
                      {step.privateNote && <div className="organizer-note"><LockKeyhole size={11} /> {step.privateNote}</div>}
                      {editing && (
                        <div className="step-actions">
                          <button className="icon-button small" disabled={busy || index === 0} onClick={() => move(step, -1)} aria-label="Færa upp"><ArrowUp size={15} /></button>
                          <button className="icon-button small" disabled={busy || index === group.steps.length - 1} onClick={() => move(step, 1)} aria-label="Færa niður"><ArrowDown size={15} /></button>
                          <button className="icon-button small" disabled={busy} onClick={() => onEditStep(step)} aria-label="Breyta lið"><Pencil size={15} /></button>
                          <button className="icon-button small danger" disabled={busy} onClick={() => onDeleteStep(step)} aria-label="Eyða lið"><Trash2 size={15} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="day-empty">Ekkert skipulagt enn.</p>}
          </div>
        )) : <Empty title="Frjáls upplifun" text={experience.canManage ? "Bættu við fyrsta dagskrárliðnum þegar þið vitið hvað á að gera." : "Engin föst dagskrá hefur verið skráð."} action={experience.canManage ? <button className="button button-primary" onClick={onAddStep}><Plus size={15} /> Bæta við lið</button> : undefined} />}
      </section>

      <section className="detail-section">
        <div className="section-row"><div><p className="mini-label">Eftir á</p><h2 className="section-title small-title">Minningarnar okkar</h2></div><button className="button button-ghost" onClick={onReflect}><Plus size={15} /> Mín skráning</button></div>
        <div className="reflection-grid">
          {experience.reflections.length ? experience.reflections.map((reflection) => (
            <article className="memory-card" key={reflection.id}>
              <div className="section-row"><strong>{reflection.authorName}</strong><span className="stars">{"★".repeat(reflection.rating)}{"☆".repeat(5 - reflection.rating)}</span></div>
              {reflection.bestPart && <><span className="memory-label">Besti hlutinn</span><p>{reflection.bestPart}</p></>}
              {reflection.memory && <><span className="memory-label">Minning</span><p>{reflection.memory}</p></>}
            </article>
          )) : <Empty title="Minningarnar bíða" text="Hver og einn skráir sína upplifun þegar dagurinn er liðinn." />}
        </div>
      </section>
    </>
  );
}

function LinkedText({ text }: { text: string }) {
  const host = (url: string) => {
    try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
  };
  return <>{text.split(/(https?:\/\/[^\s]+)/g).map((part, index) => /^https?:\/\//.test(part) ? <a key={index} href={part} target="_blank" rel="noreferrer">{host(part)} ↗</a> : part)}</>;
}

function AiPlanModal({ onClose, onSave }: { onClose: () => void; onSave: (wish: AiWish, option: AiOption, plan: AiPlan) => Promise<void> }) {
  const [wish, setWish] = useState<AiWish | null>(null);
  const [options, setOptions] = useState<AiOption[]>([]);
  const [chosen, setChosen] = useState<AiOption | null>(null);
  const [plan, setPlan] = useState<AiPlan | null>(null);
  const [waiting, setWaiting] = useState("");
  const [error, setError] = useState("");

  async function attempt(message: string, action: () => Promise<void>) {
    setWaiting(message);
    setError("");
    try {
      await action();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setWaiting("");
    }
  }

  function ask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next: AiWish = {
      osk: String(form.get("osk")),
      timabil: String(form.get("timabil")),
      naetur: Number(form.get("naetur")) || 3,
      hamarksFlugtimi: Number(form.get("hamarksFlugtimi")) || 4,
      ferdalangar: Number(form.get("ferdalangar")) || 2,
    };
    setWish(next);
    void attempt("Leita að borgum og flugi… þetta tekur 1–3 mínútur.", async () => setOptions(await suggestTrips(next)));
  }

  function choose(option: AiOption) {
    if (!wish) return;
    setChosen(option);
    void attempt(`Finn hótel og bý til dagskrá í ${option.borg}… þetta tekur 1–3 mínútur.`, async () => setPlan(await planTrip(wish, option)));
  }

  const body = waiting ? (
    <div className="empty"><Sparkles size={20} /><strong>Gervigreindin er að vinna</strong><p>{waiting}</p></div>
  ) : plan && chosen && wish ? (
    <>
      <p className="subtitle">{plan.inngangur}</p>
      <p className="mini-label">Hótel</p>
      {plan.hotel.map((hotel) => <div className="soft-row" key={hotel.nafn}><span className="soft-icon"><MapPin size={17} /></span><div><h3>{hotel.nafn}</h3><p className="step-note">{hotel.hverfi} · {hotel.verdhugmynd}{"\n"}{hotel.afhverju}{hotel.slod && <>{"\n"}<LinkedText text={hotel.slod} /></>}</p></div></div>)}
      {plan.dagar.map((day, index) => (
        <div className="day-group" key={day.dagur}>
          <h3 className="day-label">Dagur {index + 1} · {weekdayLabel(day.dagur)}</h3>
          <div className="timeline">{day.lidir.map((item, itemIndex) => <div className="timeline-step" key={itemIndex}><div className="time-label">{item.klukkan}</div><div><span className="chapter">{item.kafli}</span><h3>{item.titill}</h3><p className="step-note"><LinkedText text={[item.lysing, item.slod].filter(Boolean).join("\n")} /></p></div></div>)}</div>
        </div>
      ))}
      {error && <p className="error-message">{error}</p>}
      <div className="form-actions"><button type="button" className="button button-ghost" onClick={() => setPlan(null)}>Velja aðra borg</button><button type="button" className="button button-primary" onClick={() => void attempt("Vista…", () => onSave(wish, chosen, plan))}>Vista sem upplifun</button></div>
    </>
  ) : options.length ? (
    <>
      <p className="subtitle">Veldu borg. Bókanir gerið þið sjálf með hlekkjunum.</p>
      {options.map((option) => (
        <button type="button" className="experience-card" key={option.borg} onClick={() => choose(option)}>
          <span className="date-block"><Plane size={18} /><span>{option.flug.flugtimi}</span></span>
          <span className="experience-copy">
            <h3>{option.borg}, {option.land}</h3>
            <p>{option.afhverju} {option.hapunktar}</p>
            <span className="pill-row"><span className="pill"><CalendarDays size={11} /> {formatDateRange(option.dagsetningar.fra, option.dagsetningar.til)}</span><span className="pill">{option.flug.flugfelog}{option.flug.beint ? " · beint" : ""}</span>{option.flug.verdhugmynd && <span className="pill">{option.flug.verdhugmynd}</span>}</span>
          </span>
          <ChevronRight size={19} />
        </button>
      ))}
      {error && <p className="error-message">{error}</p>}
      <div className="form-actions"><button type="button" className="button button-ghost" onClick={() => setOptions([])}>Breyta ósk</button></div>
    </>
  ) : (
    <form onSubmit={ask}>
      <div className="field"><label htmlFor="osk">Hvað langar ykkur?</label><textarea id="osk" name="osk" required maxLength={800} defaultValue={wish?.osk} placeholder="t.d. Fara í jólaþorp í einhverri borg, rólegt og notalegt, góður matur og kannski spa." /></div>
      <div className="field"><label htmlFor="timabil">Hvenær?</label><input id="timabil" name="timabil" required maxLength={120} defaultValue={wish?.timabil} placeholder="t.d. mánaðamót nóvember og desember" /></div>
      <div className="field-grid">
        <div className="field"><label htmlFor="naetur">Nætur</label><input id="naetur" name="naetur" type="number" min={1} max={14} defaultValue={wish?.naetur ?? 3} /></div>
        <div className="field"><label htmlFor="hamarksFlugtimi">Mesti flugtími (klst)</label><input id="hamarksFlugtimi" name="hamarksFlugtimi" type="number" min={1} max={15} defaultValue={wish?.hamarksFlugtimi ?? 4} /></div>
        <div className="field"><label htmlFor="ferdalangar">Fjöldi</label><input id="ferdalangar" name="ferdalangar" type="number" min={1} max={10} defaultValue={wish?.ferdalangar ?? 2} /></div>
      </div>
      {error && <p className="error-message">{error}</p>}
      <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Hætta við</button><button className="button button-primary"><Sparkles size={16} /> Finna borgir</button></div>
    </form>
  );

  return <Modal eyebrow="Gervigreind skipuleggur" title={plan ? plan.titill : options.length ? "Þrjár tillögur" : "Ný ferð"} onClose={onClose}>{body}</Modal>;
}

function GardenView({ data, onNewIdea }: { data: AppData; onNewIdea: () => void }) {
  const [query, setQuery] = useState("");
  const plants = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("is");
    if (!needle) return data.plantSpecies;
    return data.plantSpecies.filter((plant) => `${plant.commonName} ${plant.scientificName ?? ""} ${plant.notes}`.toLocaleLowerCase("is").includes(needle));
  }, [data.plantSpecies, query]);
  return (
    <>
      <PageHeading eyebrow="Okkar garður" title="Garðurinn" text="Svæðin, plönturnar og hugmyndirnar í lifandi yfirliti sem má þróast með garðinum." action={<button className="button button-primary" onClick={onNewIdea}><Plus size={16} /> Hugmynd</button>} />
      <section className="garden-hero">
        <div><span className="hero-kicker"><Leaf size={12} /> Garður í mótun</span><h2>Frá hjartabeði að framtíðardraumum.</h2><p>{data.gardenZones.length} svæði eru skráð. Hægt er að varðveita nöfnin, söguna og næstu skref á einum stað.</p></div>
        <div className="garden-art" aria-hidden="true"><span className="a">Hjartabeð</span><span className="b">Gróðurhús</span><span className="c">Matjurtir</span><span className="d">Tjörn</span></div>
      </section>

      <section className="section-block">
        <div className="section-row"><div><p className="mini-label">Skipulag</p><h2 className="section-title small-title">Svæðin okkar</h2></div><span className="count-badge">{data.gardenZones.length}</span></div>
        <div className="zone-grid">
          {data.gardenZones.map((zone) => <article className="zone-card" key={zone.id}><span className="zone-icon">{zone.symbol || "✦"}</span><div><h3>{zone.name}</h3><p>{zone.dimensions || zone.description || "Nánari lýsing bíður."}</p></div></article>)}
        </div>
      </section>

      <section className="section-block">
        <div className="section-row"><div><p className="mini-label">Plöntusafn</p><h2 className="section-title small-title">Plönturnar</h2></div></div>
        <label className="searchbox"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Leita að plöntu…" /></label>
        <div className="plant-grid">
          {plants.map((plant, index) => {
            const planting = data.gardenPlantings.find((item) => item.plantSpeciesId === plant.id);
            const zone = data.gardenZones.find((item) => item.id === planting?.zoneId);
            return <article className="plant" key={plant.id}><div className="plant-art">{["❀", "♣", "❧", "✿"][index % 4]}</div><div className="plant-body"><h3>{plant.commonName}</h3><p>{zone?.name || planting?.positionNote || plant.scientificName || plant.notes || "Staðsetning óskráð"}</p></div></article>;
          })}
        </div>
      </section>

      <section className="card card-pad section-block">
        <div className="section-row"><div><p className="mini-label">Hugmyndabanki</p><h2 className="section-title small-title">Næst í garðinum</h2></div><button className="button button-ghost" onClick={onNewIdea}><Plus size={15} /> Bæta við</button></div>
        {data.gardenIdeas.length ? <div>{data.gardenIdeas.map((idea) => <div className="soft-row" key={idea.id}><span className="soft-icon"><Sprout size={17} /></span><div><h3>{idea.text}</h3><p>{idea.authorName} · {formatDate(idea.createdAt)}</p></div></div>)}</div> : <Empty title="Hugmyndabankinn er opinn" text="Skráðu hugmynd um leið og hún kviknar." />}
      </section>
    </>
  );
}

function FishingView({ data, onWater, onCatch }: { data: AppData; onWater: () => void; onCatch: () => void }) {
  return (
    <>
      <PageHeading eyebrow="Við vatnið" title="Veiði" text="Veiðistaðir, aðstæður og afli skráð á nokkrum sekúndum — svo reynslan nýtist næst." action={<button className="button button-primary" onClick={onCatch}><Fish size={16} /> Skrá afla</button>} />
      <section className="fishing-hero">
        <div><span className="hero-kicker"><Waves size={13} /> Veiðibókin</span><h2>Aðstæður verða að reynslu.</h2><p>Skráðu vatn, veður og það sem virkaði meðan minnið er ferskt.</p><div className="hero-actions"><button className="button button-light" onClick={onWater}><Waves size={16} /> Skrá aðstæður</button><button className="button button-hero-ghost" onClick={onCatch}><Fish size={16} /> Skrá afla</button></div></div>
        <div className="fishing-stats"><div><strong>{data.fishingLocations.length}</strong><span>staðir</span></div><div><strong>{data.waterObservations.length}</strong><span>athuganir</span></div><div><strong>{data.catchLogs.length}</strong><span>aflar</span></div></div>
      </section>

      <section className="section-block">
        <div className="section-row"><div><p className="mini-label">Kortið okkar</p><h2 className="section-title small-title">Veiðistaðir</h2></div></div>
        <div className="location-grid">
          {data.fishingLocations.map((location) => <article className="location-card" key={location.id}><span><MapPin size={20} /></span><div><h3>{location.name}</h3><p>{location.waterSystem || location.notes || "Persónulegur veiðistaður"}</p></div></article>)}
        </div>
      </section>

      <div className="grid-two fishing-columns">
        <section className="card card-pad">
          <div className="section-row"><p className="mini-label">Nýjustu aðstæður</p><button className="text-button" onClick={onWater}>Skrá</button></div>
          {data.waterObservations.length ? data.waterObservations.slice(0, 4).map((item) => <div className="log-row" key={item.id}><span className="log-icon water"><Waves size={17} /></span><div><h3>{item.locationName}</h3><p>{[item.waterLevel, item.waterColor, item.flow].filter(Boolean).join(" · ") || "Aðstæður skráðar"}</p><small>{formatDate(item.observedAt, true)}</small></div></div>) : <Empty title="Engar aðstæður skráðar" text="Taktu stöðuna áður en þú byrjar." />}
        </section>
        <section className="card card-pad">
          <div className="section-row"><p className="mini-label">Aflaskrá</p><button className="text-button" onClick={onCatch}>Skrá</button></div>
          {data.catchLogs.length ? data.catchLogs.slice(0, 4).map((item) => <div className="log-row" key={item.id}><span className="log-icon fish"><Fish size={17} /></span><div><h3>{item.species}</h3><p>{item.locationName}{item.lengthCm ? ` · ${item.lengthCm} cm` : ""}{item.weightKg ? ` · ${item.weightKg} kg` : ""}</p><small>{item.released ? "Sleppt" : "Haldið"} · {formatDate(item.caughtAt, true)}</small></div></div>) : <Empty title="Aflaskráin bíður" text="Skráning tekur innan við hálfa mínútu." />}
        </section>
      </div>
    </>
  );
}

function MoreView({ data, email, onInvite, onSignOut }: { data: AppData; email: string; onInvite: () => void; onSignOut: () => void }) {
  const canInvite = data.role === "owner" || data.role === "admin";
  return (
    <>
      <PageHeading eyebrow="Stillingar" title={data.workspace.name} text="Meðlimir, virkar einingar og þinn aðgangur." action={canInvite ? <button className="button button-primary" onClick={onInvite}><Plus size={16} /> Bjóða</button> : undefined} />
      <div className="grid-two">
        <section className="card card-pad">
          <p className="mini-label">Fólkið okkar</p>
          <div className="member-list">{data.members.map((member) => <div className="profile-card" key={member.userId}><span className="avatar">{initials(member.displayName)}</span><div><h3>{member.displayName}</h3><p>{member.email}</p><span className="role-badge">{member.role === "owner" ? "Eigandi" : member.role === "admin" ? "Stjórnandi" : "Meðlimur"}</span></div></div>)}</div>
          {canInvite && <button className="button button-ghost full top-gap" onClick={onInvite}><Mail size={16} /> Bjóða með netfangi</button>}
        </section>
        <section className="card card-pad">
          <p className="mini-label">Virkar einingar</p>
          <div className="module-list">{data.modules.map((module) => { const Icon = module === "core" ? Heart : module === "experiences" ? Sparkles : module === "garden" ? Sprout : Fish; return <div className="module-row" key={module}><span><Icon size={17} /></span><strong>{moduleNames[module]}</strong><Check size={16} /></div>; })}</div>
          <p className="fine-print module-note">Einingarnar eru aðskildar í gagnagrunni. Síðar má veita eða selja aðgang að einni, nokkrum eða öllum.</p>
        </section>
      </div>
      <section className="card card-pad section-block account-row"><div><p className="mini-label">Þinn aðgangur</p><h3>{email}</h3><p>Innskráning með öruggum tölvupóststengli.</p></div><button className="button button-ghost" onClick={onSignOut}><LogOut size={16} /> Skrá út</button></section>
    </>
  );
}

function Empty({ title, text, action }: { title: string; text: string; action?: ReactNode }) {
  return <div className="empty"><strong>{title}</strong><p>{text}</p>{action}</div>;
}

function Modal({ title, eyebrow, onClose, children }: { title: string; eyebrow: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.classList.add("modal-open");
    return () => { document.removeEventListener("keydown", onKey); document.body.classList.remove("modal-open"); };
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head"><div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div><button className="icon-button" onClick={onClose} aria-label="Loka"><X size={18} /></button></div>
        {children}
      </section>
    </div>
  );
}

function FormActions({ busy, onClose, submitLabel = "Vista" }: { busy: boolean; onClose: () => void; submitLabel?: string }) {
  return <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Hætta við</button><button className="button button-primary" disabled={busy}>{busy ? "Vista…" : submitLabel}</button></div>;
}

function NewExperienceModal({ busy, error, onClose, onSubmit }: { busy: boolean; error: string; onClose: () => void; onSubmit: (input: ExperienceInput & { visibility: ExperienceVisibility }) => void }) {
  return (
    <Modal eyebrow="Ný hugmynd" title="Skrá upplifun" onClose={onClose}>
      <form onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); onSubmit({ ...experienceInputFrom(form), visibility: String(form.get("visibility")) as ExperienceVisibility }); }}>
        <ExperienceFields />
        <fieldset className="field"><legend>Hver sér þetta?</legend><div className="radio-grid"><label className="radio-card"><input type="radio" name="visibility" value="shared" defaultChecked /><span><strong>Okkar — sýnilegt báðum</strong><small>Bæði sjá þetta strax.</small></span></label><label className="radio-card"><input type="radio" name="visibility" value="surprise" /><span><strong>Óvænt — deila síðar</strong><small>Aðeins þú sérð þar til þú afhjúpar.</small></span></label><label className="radio-card"><input type="radio" name="visibility" value="private" /><span><strong>Bara ég</strong><small>Persónuleg hugmynd.</small></span></label></div></fieldset>
        {error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} submitLabel="Vista upplifun" />
      </form>
    </Modal>
  );
}

function experienceInputFrom(form: FormData): ExperienceInput {
  return {
    title: String(form.get("title")),
    occasion: String(form.get("occasion")),
    eventDate: String(form.get("eventDate")),
    endDate: String(form.get("endDate") || "") || null,
    introText: String(form.get("introText")),
  };
}

function ExperienceFields({ experience }: { experience?: Experience }) {
  const [start, setStart] = useState(experience?.eventDate ?? todayValue());
  return (
    <>
      <div className="field"><label htmlFor="title">Heiti</label><input id="title" name="title" required maxLength={120} defaultValue={experience?.title} placeholder="t.d. Helgi í Hvalfirði" /></div>
      <div className="field"><label htmlFor="occasion">Tilefni</label><input id="occasion" name="occasion" maxLength={80} defaultValue={experience?.occasion} placeholder="Afmæli, ferðalag, frí…" /></div>
      <div className="field-grid">
        <div className="field"><label htmlFor="eventDate">Fyrsti dagur</label><input id="eventDate" name="eventDate" type="date" required value={start} onChange={(event) => setStart(event.target.value)} /></div>
        <div className="field"><label htmlFor="endDate">Síðasti dagur <small>(ef fleiri dagar)</small></label><input id="endDate" name="endDate" type="date" min={start} defaultValue={experience?.endDate ?? ""} /></div>
      </div>
      <div className="field"><label htmlFor="introText">Stutt lýsing</label><textarea id="introText" name="introText" maxLength={1200} defaultValue={experience?.introText} placeholder="Hvað langar ykkur að upplifa?" /></div>
    </>
  );
}

function EditExperienceModal({ experience, busy, error, onClose, onSubmit }: { experience: Experience; busy: boolean; error: string; onClose: () => void; onSubmit: (input: ExperienceInput) => void }) {
  return (
    <Modal eyebrow={experience.title} title="Breyta upplifun" onClose={onClose}>
      <form onSubmit={(event) => { event.preventDefault(); onSubmit(experienceInputFrom(new FormData(event.currentTarget))); }}>
        <ExperienceFields experience={experience} />
        {error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} />
      </form>
    </Modal>
  );
}

function StepModal({ experience, step, busy, error, onClose, onSubmit }: { experience: Experience; step: ExperienceStep | null; busy: boolean; error: string; onClose: () => void; onSubmit: (input: ExperienceStepInput) => void }) {
  const days = experienceDays(experience);
  return (
    <Modal eyebrow={experience.title} title={step ? "Breyta lið" : "Nýr dagskrárliður"} onClose={onClose}>
      <form onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        onSubmit({
          dayDate: days.length > 1 ? String(form.get("dayDate") || "") || null : null,
          timeLabel: String(form.get("timeLabel")),
          chapter: String(form.get("chapter")),
          title: String(form.get("stepTitle")),
          note: String(form.get("note")),
        });
      }}>
        <div className="field"><label htmlFor="stepTitle">Hvað</label><input id="stepTitle" name="stepTitle" required maxLength={160} defaultValue={step?.title} placeholder="t.d. Kvöldmatur á Hótel Glym" /></div>
        <div className="field-grid">
          {days.length > 1 && <div className="field"><label htmlFor="dayDate">Dagur</label><select id="dayDate" name="dayDate" defaultValue={step?.dayDate ?? days[0]}>{days.map((day, index) => <option key={day} value={day}>Dagur {index + 1} · {weekdayLabel(day)}</option>)}</select></div>}
          <div className="field"><label htmlFor="timeLabel">Klukkan</label><input id="timeLabel" name="timeLabel" maxLength={40} defaultValue={step?.timeLabel} placeholder="t.d. 18:00" /></div>
        </div>
        <div className="field"><label htmlFor="chapter">Kafli <small>(valfrjálst)</small></label><input id="chapter" name="chapter" maxLength={80} defaultValue={step?.chapter} placeholder="t.d. Morgunn, Ferðin heim" /></div>
        <div className="field"><label htmlFor="note">Nánar</label><textarea id="note" name="note" maxLength={1200} defaultValue={step?.note} placeholder="Heimilisfang, hvað á að taka með…" /></div>
        {error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} submitLabel={step ? "Vista" : "Bæta við"} />
      </form>
    </Modal>
  );
}

function ReflectionModal({ experience, busy, error, onClose, onSubmit }: { experience: Experience; busy: boolean; error: string; onClose: () => void; onSubmit: (input: { rating: number; bestPart: string; feeling: string; pace: string; repeatWish: string; changeNext: string; memory: string }) => void }) {
  const mine = experience.reflections.find((item) => item.isMine);
  return (
    <Modal eyebrow={experience.title} title="Mín minning" onClose={onClose}>
      <form onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); onSubmit({ rating: Number(form.get("rating")), bestPart: String(form.get("bestPart")), feeling: String(form.get("feeling")), pace: String(form.get("pace")), repeatWish: String(form.get("repeatWish")), changeNext: String(form.get("changeNext")), memory: String(form.get("memory")) }); }}>
        <fieldset className="field"><legend>Hvernig var dagurinn?</legend><div className="rating-row">{[1, 2, 3, 4, 5].map((rating) => <label key={rating}><input type="radio" name="rating" value={rating} defaultChecked={rating === (mine?.rating ?? 5)} /><span><Star size={19} fill="currentColor" /></span></label>)}</div></fieldset>
        <div className="field"><label htmlFor="bestPart">Besti hlutinn</label><textarea id="bestPart" name="bestPart" defaultValue={mine?.bestPart} placeholder="Hvað stendur upp úr?" /></div>
        <div className="field-grid"><div className="field"><label htmlFor="feeling">Tilfinningin</label><input id="feeling" name="feeling" defaultValue={mine?.feeling} placeholder="Ró, gleði…" /></div><div className="field"><label htmlFor="pace">Hraðinn</label><input id="pace" name="pace" defaultValue={mine?.pace} placeholder="Akkúrat, of þétt…" /></div></div>
        <div className="field"><label htmlFor="repeatWish">Hvað vil ég endurtaka?</label><textarea id="repeatWish" name="repeatWish" defaultValue={mine?.repeatWish} /></div>
        <div className="field"><label htmlFor="changeNext">Hverju myndi ég breyta næst?</label><textarea id="changeNext" name="changeNext" defaultValue={mine?.changeNext} /></div>
        <div className="field"><label htmlFor="memory">Minning sem ég vil varðveita</label><textarea id="memory" name="memory" defaultValue={mine?.memory} /></div>
        {error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} submitLabel="Vista mína minningu" />
      </form>
    </Modal>
  );
}

function GardenIdeaModal({ busy, error, onClose, onSubmit }: { busy: boolean; error: string; onClose: () => void; onSubmit: (text: string) => void }) {
  return <Modal eyebrow="Hugmyndabanki" title="Ný garðhugmynd" onClose={onClose}><form onSubmit={(event) => { event.preventDefault(); onSubmit(String(new FormData(event.currentTarget).get("text"))); }}><div className="field"><label htmlFor="idea-text">Hugmynd</label><textarea id="idea-text" name="text" required autoFocus maxLength={800} placeholder="t.d. Setja bekk við tjörnina…" /></div>{error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} submitLabel="Vista hugmynd" /></form></Modal>;
}

type LocationOption = AppData["fishingLocations"][number];

function LocationSelect({ locations }: { locations: LocationOption[] }) {
  return <select name="locationId" required defaultValue=""><option value="" disabled>Veldu stað</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select>;
}

function WaterModal({ locations, busy, error, onClose, onSubmit }: { locations: LocationOption[]; busy: boolean; error: string; onClose: () => void; onSubmit: (input: { locationId: string; observedAt: string; tidePhase: string; waterLevel: string; waterColor: string; flow: string; weather: string; notes: string }) => void }) {
  return <Modal eyebrow="Veiðibókin" title="Skrá aðstæður" onClose={onClose}><form onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); onSubmit({ locationId: String(form.get("locationId")), observedAt: String(form.get("observedAt")), tidePhase: String(form.get("tidePhase")), waterLevel: String(form.get("waterLevel")), waterColor: String(form.get("waterColor")), flow: String(form.get("flow")), weather: String(form.get("weather")), notes: String(form.get("notes")) }); }}><div className="field-grid"><div className="field"><label>Staður</label><LocationSelect locations={locations} /></div><div className="field"><label>Tími</label><input type="datetime-local" name="observedAt" required defaultValue={localDateTimeValue()} /></div></div><div className="field-grid three"><div className="field"><label>Vatnshæð</label><input name="waterLevel" placeholder="Lítil, meðal…" /></div><div className="field"><label>Litur</label><input name="waterColor" placeholder="Tært, litað…" /></div><div className="field"><label>Rennsli</label><input name="flow" placeholder="Hægt, gott…" /></div></div><div className="field-grid"><div className="field"><label>Flóð/fjara</label><input name="tidePhase" placeholder="Fallandi, hækkandi…" /></div><div className="field"><label>Veður</label><input name="weather" placeholder="6°C, hægur vindur…" /></div></div><div className="field"><label>Athugasemd</label><textarea name="notes" placeholder="Það sem gæti skipt máli næst…" /></div>{error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} submitLabel="Vista aðstæður" /></form></Modal>;
}

function CatchModal({ locations, busy, error, onClose, onSubmit }: { locations: LocationOption[]; busy: boolean; error: string; onClose: () => void; onSubmit: (input: { locationId: string; caughtAt: string; species: string; lengthCm: number | null; weightKg: number | null; lure: string; released: boolean; notes: string }) => void }) {
  return <Modal eyebrow="Aflaskrá" title="Skrá afla" onClose={onClose}><form onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); const length = String(form.get("lengthCm")); const weight = String(form.get("weightKg")); onSubmit({ locationId: String(form.get("locationId")), caughtAt: String(form.get("caughtAt")), species: String(form.get("species")), lengthCm: length ? Number(length) : null, weightKg: weight ? Number(weight) : null, lure: String(form.get("lure")), released: form.get("released") === "on", notes: String(form.get("notes")) }); }}><div className="field-grid"><div className="field"><label>Staður</label><LocationSelect locations={locations} /></div><div className="field"><label>Tími</label><input type="datetime-local" name="caughtAt" required defaultValue={localDateTimeValue()} /></div></div><div className="field-grid"><div className="field"><label>Tegund</label><input name="species" required defaultValue="Sjóbirtingur" /></div><div className="field"><label>Beita</label><input name="lure" placeholder="Fluga, spúnn…" /></div></div><div className="field-grid"><div className="field"><label><Ruler size={13} /> Lengd (cm)</label><input type="number" inputMode="decimal" name="lengthCm" min="0" step="0.1" /></div><div className="field"><label><Scale size={13} /> Þyngd (kg)</label><input type="number" inputMode="decimal" name="weightKg" min="0" step="0.01" /></div></div><label className="check-card"><input type="checkbox" name="released" defaultChecked /><span><strong>Fiskinum sleppt</strong><small>Merkt sem veitt og sleppt.</small></span></label><div className="field"><label>Athugasemd</label><textarea name="notes" placeholder="Staður í hylnum, hegðun, annað…" /></div>{error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} submitLabel="Vista afla" /></form></Modal>;
}

function InviteModal({ busy, error, onClose, onSubmit }: { busy: boolean; error: string; onClose: () => void; onSubmit: (email: string, role: "admin" | "member" | "guest") => void }) {
  return <Modal eyebrow="Sameiginlegt rými" title="Bjóða meðlim" onClose={onClose}><form onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); onSubmit(String(form.get("email")), String(form.get("role")) as "admin" | "member" | "guest"); }}><p className="modal-copy">Viðkomandi skráir sig inn með þessu netfangi og tengist sjálfkrafa rýminu.</p><div className="field"><label htmlFor="invite-email">Netfang</label><input id="invite-email" name="email" type="email" inputMode="email" autoComplete="email" required placeholder="nafn@netfang.is" /></div><div className="field"><label htmlFor="invite-role">Aðgangur</label><select id="invite-role" name="role" defaultValue="member"><option value="member">Meðlimur — getur skráð og skoðað</option><option value="guest">Gestur — takmarkað hlutverk</option><option value="admin">Stjórnandi — getur líka boðið öðrum</option></select></div>{error && <p className="error-message">{error}</p>}<FormActions busy={busy} onClose={onClose} submitLabel="Stofna boð" /></form></Modal>;
}
